import React, { useState, useEffect } from 'react';
import { X, FileText, Download, User, Calendar, CheckCircle, Clock } from 'lucide-react';
import axios from 'axios';

const ViewSubmissionsModal = ({ assignment, onClose }) => {
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [gradingSubmission, setGradingSubmission] = useState(null);
    const [gradeData, setGradeData] = useState({ grade: '', feedback: '' });
    const [savingGrade, setSavingGrade] = useState(false);

    useEffect(() => {
        if (assignment) {
            fetchSubmissions();
        }
    }, [assignment]);

    const fetchSubmissions = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/assignment-submissions/assignment/${assignment.id}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            setSubmissions(response.data);
        } catch (err) {
            console.error(err);
            setError('Failed to load submissions.');
        } finally {
            setLoading(false);
        }
    };

    const handleGradeClick = (submission) => {
        setGradingSubmission(submission);
        setGradeData({
            grade: submission.grade || '',
            feedback: submission.feedback || ''
        });
    };

    const handleSaveGrade = async (e) => {
        e.preventDefault();
        setSavingGrade(true);
        try {
            await axios.put(`${process.env.REACT_APP_API_BASE_URL}/assignment-submissions/${gradingSubmission.id}/grade`, {
                grade: gradeData.grade,
                feedback: gradeData.feedback
            }, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });

            // Update local state
            setSubmissions(prev => prev.map(sub =>
                sub.id === gradingSubmission.id
                    ? { ...sub, grade: gradeData.grade, feedback: gradeData.feedback, status: 'graded' }
                    : sub
            ));
            setGradingSubmission(null);
        } catch (err) {
            alert('Failed to save grade: ' + (err.response?.data?.message || err.message));
        } finally {
            setSavingGrade(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-100">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800">Submissions</h2>
                        <p className="text-sm text-gray-500 mt-1">{assignment.title} • {assignment.points} Points</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100">
                        <X size={24} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-hidden flex">
                    {/* List View */}
                    <div className={`flex-1 overflow-y-auto p-6 ${gradingSubmission ? 'hidden md:block md:w-1/2 border-r border-gray-200' : 'w-full'}`}>
                        {loading ? (
                            <div className="flex justify-center py-12">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            </div>
                        ) : error ? (
                            <div className="text-red-500 text-center py-8">{error}</div>
                        ) : submissions.length === 0 ? (
                            <div className="text-center py-12 text-gray-500">
                                <FileText className="w-12 h-12 mx-auto mb-3 opacity-20" />
                                <p>No submissions yet.</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {submissions.map(sub => (
                                    <div
                                        key={sub.id}
                                        className={`border rounded-lg p-4 cursor-pointer transition-colors ${gradingSubmission?.id === sub.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                                            }`}
                                        onClick={() => handleGradeClick(sub)}
                                    >
                                        <div className="flex justify-between items-start mb-2">
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-medium">
                                                    {sub.student?.name?.charAt(0) || 'S'}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-900">{sub.student?.name || 'Unknown Student'}</p>
                                                    <p className="text-xs text-gray-500">{sub.student?.email}</p>
                                                </div>
                                            </div>
                                            <div className={`text-xs px-2 py-1 rounded-full font-medium ${sub.status === 'graded' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                                }`}>
                                                {sub.status === 'graded' ? `${sub.grade}/${assignment.points}` : 'Needs Grading'}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4 text-xs text-gray-500">
                                            <span className="flex items-center gap-1">
                                                <Calendar size={12} /> {formatDate(sub.submissionDate)}
                                            </span>
                                            {sub.isLate && (
                                                <span className="text-red-500 font-medium bg-red-50 px-1.5 py-0.5 rounded">Late</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Grading Pane */}
                    {gradingSubmission && (
                        <div className="flex-1 overflow-y-auto p-6 bg-gray-50 md:bg-white inset-0 fixed md:static z-50 md:z-auto">
                            <div className="flex justify-between items-center md:hidden mb-6">
                                <h3 className="font-bold text-lg">Grading</h3>
                                <button onClick={() => setGradingSubmission(null)} className="text-gray-500">
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="space-y-6">
                                {/* Submission Content */}
                                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                                    <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                                        <FileText size={16} /> Submission Content
                                    </h4>

                                    {gradingSubmission.submissionText && (
                                        <div className="bg-gray-50 p-3 rounded text-sm text-gray-800 whitespace-pre-wrap mb-4">
                                            {gradingSubmission.submissionText}
                                        </div>
                                    )}

                                    {gradingSubmission.submissionFile && (
                                        <div className="flex items-center gap-3 p-3 border border-blue-100 bg-blue-50 rounded-lg">
                                            <FileText className="text-blue-600" size={20} />
                                            <div className="flex-1 overflow-hidden">
                                                <p className="text-sm font-medium text-blue-900 truncate">
                                                    {gradingSubmission.submissionFile.split(/[/\\]/).pop().split('-').slice(3).join('-') || 'Attached File'}
                                                </p>
                                            </div>
                                            <button
                                                onClick={async () => {
                                                    try {
                                                        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/assignment-submissions/${gradingSubmission.id}/download`, {
                                                            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                                                            responseType: 'blob'
                                                        });

                                                        // Create a blob URL and trigger download
                                                        const blob = new Blob([response.data], {
                                                            type: response.headers['content-type'] || gradingSubmission.fileType
                                                        });
                                                        const url = window.URL.createObjectURL(blob);
                                                        const link = document.createElement('a');
                                                        link.href = url;
                                                        link.setAttribute('download', gradingSubmission.fileName || 'submission.bin');
                                                        document.body.appendChild(link);
                                                        link.click();
                                                        link.remove();
                                                        window.URL.revokeObjectURL(url);
                                                    } catch (err) {
                                                        console.error(err);
                                                        alert('Download failed. File may be missing.');
                                                    }
                                                }}
                                                className="text-white bg-blue-600 hover:bg-blue-700 p-2 rounded-lg flex items-center gap-2 text-xs font-medium transition-colors"
                                            >
                                                <Download size={14} /> Download
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Grading Form */}
                                <form onSubmit={handleSaveGrade} className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                                    <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                                        <CheckCircle size={16} /> Grade & Feedback
                                    </h4>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Grade (out of {assignment.points})
                                        </label>
                                        <input
                                            type="number"
                                            max={assignment.points}
                                            min="0"
                                            required
                                            value={gradeData.grade}
                                            onChange={(e) => setGradeData(prev => ({ ...prev, grade: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Feedback
                                        </label>
                                        <textarea
                                            rows="4"
                                            value={gradeData.feedback}
                                            onChange={(e) => setGradeData(prev => ({ ...prev, feedback: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                            placeholder="Enter detailed feedback..."
                                        />
                                    </div>

                                    <div className="flex gap-2 justify-end">
                                        <button
                                            type="button"
                                            onClick={() => setGradingSubmission(null)}
                                            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={savingGrade}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                        >
                                            {savingGrade ? 'Saving...' : 'Save Grade'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ViewSubmissionsModal;
